import { ApolloProvider } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import { MonthlyDashboard } from "components/PortfolioDashboard/Pages/MonthlyDashboard";
import { YearlyDashboard } from "components/PortfolioDashboard/Pages/YearlyDashboard";
import { ResetPassword } from "components/ResetPassword";
import { SetPassword } from "components/SetPassword";
import Toast from "components/Toastify/Toast";
import { ReportsFramework } from "containers/ReportsListPage/ReportsFramework";
import { ReportsSaved } from "containers/ReportsListPage/ReportsSaved";
import { StrategyPage } from "containers/StrategyPage";
import { DashboardLayout } from "legacyNext/layouts";
import { LegacyNextRoutes, HubRoute } from "legacyNext/navigation";
import { Login, Home, AssetPageLayout } from "legacyNext/pages";
import {
    Overview,
    Sustainability,
    Consumption,
    Economy,
} from "legacyNext/pages/Asset";
import {
    AssetRisk,
    LegacyMap,
    SearchList,
} from "legacyNext/pages/Home/components";
import { LegacyNextStoreProvider } from "legacyNext/store";
import {
    NotFoundPage,
    ChangeLogPage,
    ThemePage,
    DataOverviewPage,
} from "pages";

import { LegacyNextWrapper } from "./LegacyNextWrapper";
import AssessmentRoute from "./Routing/AssessmentRoute";
import NavRoute from "./Routing/NavRoute";
import NoAuthRoute from "./Routing/NoAuthRoute";
import client from "../../utils/apolloClient";
import AssessmentOnboardingPage from "../AssessmentOnboarding/AssessmentOnboardingPage";
import AssetListPage from "../AssetListPage/AssetListPage";
import AssetPage from "../AssetPage";
import AuthPage from "../AuthPage/AuthPage";
import CustomerImportPage from "../CustomerImportPage/CustomerImportPage";
import DataUploadPage from "../DataUploadPage/DataUploadPage";
import ForecastPage from "../ForecastPage/ForecastPage";
import IntegrationOnboarding from "../IntegrationOnboarding/IntegrationOnboardingMain";
import InternalToolboxPage from "../InternalToolboxPage/InternalToolboxPage";
import PortfolioPage from "../PortfolioPage/PortfolioPage";
import ReportPage from "../ReportPage/ReportPage";
import ReportsListPage from "../ReportsListPage/ReportsListPage";
import SettingsPage from "../SettingsPage/SettingsPage";

function App() {
    const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

    return (
        <Suspense>
            <Router>
                <ApolloProvider client={client}>
                    <SentryRoutes>
                        <Route element={<LegacyNextWrapper />}>
                            <Route
                                path="next/*"
                                element={<LegacyNextRoutes />}
                            />

                            <Route path="auth" element={<NoAuthRoute />}>
                                <Route
                                    path=":authType/:id?"
                                    element={<AuthPage />}
                                />
                                <Route>
                                    <Route
                                        path="resetpassword"
                                        element={<ResetPassword />}
                                    />
                                    <Route
                                        path="setpassword"
                                        element={<SetPassword />}
                                    />
                                </Route>
                            </Route>
                            <Route element={<NavRoute />}>
                                <Route
                                    path="integration-onboarding"
                                    element={<IntegrationOnboarding />}
                                />
                                <Route
                                    path="dev/theme"
                                    element={<ThemePage />}
                                />

                                <Route element={<AssessmentRoute />}>
                                    <Route
                                        path="settings"
                                        element={<SettingsPage />}
                                    />
                                    <Route
                                        path="change-log"
                                        element={<ChangeLogPage />}
                                    />

                                    <Route
                                        path="location-overview/:locationId/*"
                                        element={<AssetPage />}
                                    />
                                    <Route
                                        path="strategy"
                                        element={<StrategyPage />}
                                    />
                                    <Route
                                        path="forecast"
                                        element={<ForecastPage />}
                                    />
                                    <Route
                                        path="assetlist"
                                        element={<AssetListPage />}
                                    />
                                    <Route
                                        path="portfolio"
                                        element={<PortfolioPage />}
                                    >
                                        <Route
                                            index
                                            element={<MonthlyDashboard />}
                                        />
                                        <Route
                                            path="overview"
                                            element={<YearlyDashboard />}
                                        />
                                    </Route>
                                    <Route
                                        path="data-overview"
                                        element={<DataOverviewPage />}
                                    />
                                    <Route
                                        path="reports"
                                        element={<ReportsListPage />}
                                    >
                                        <Route
                                            index
                                            element={<ReportsFramework />}
                                        />
                                        <Route
                                            path="saved"
                                            element={<ReportsSaved />}
                                        />
                                    </Route>
                                    <Route
                                        path="reports/:reportType"
                                        element={<ReportPage />}
                                    />

                                    <Route
                                        path="secret"
                                        element={<InternalToolboxPage />}
                                    />
                                    <Route
                                        path="internal-data-upload"
                                        element={<DataUploadPage />}
                                    />
                                    <Route
                                        path="internal-customer-onboarding"
                                        element={<CustomerImportPage />}
                                    />
                                </Route>

                                <Route
                                    path="/"
                                    element={<AssessmentOnboardingPage />}
                                />
                            </Route>
                            <Route path="*" element={<NotFoundPage />} />
                        </Route>
                        <Route path="hub">
                            <Route path="auth/login" element={<Login />} />
                            <Route
                                element={
                                    <LegacyNextStoreProvider>
                                        <DashboardLayout>
                                            <HubRoute />
                                        </DashboardLayout>
                                    </LegacyNextStoreProvider>
                                }
                            >
                                <Route element={<Home />}>
                                    <Route index element={<LegacyMap />} />
                                    <Route
                                        path="list"
                                        element={<SearchList />}
                                    />
                                    <Route
                                        path="graph"
                                        element={<AssetRisk />}
                                    />
                                </Route>
                                <Route
                                    path="asset/:id"
                                    element={<AssetPageLayout />}
                                >
                                    <Route index element={<Overview />} />
                                    <Route
                                        path="consumption"
                                        element={<Consumption />}
                                    />
                                    <Route
                                        path="sustainability"
                                        element={<Sustainability />}
                                    />
                                    <Route
                                        path="economy"
                                        element={<Economy />}
                                    />
                                </Route>
                            </Route>
                        </Route>
                    </SentryRoutes>

                    <Toast />
                </ApolloProvider>
            </Router>
        </Suspense>
    );
}

export default App;
